import React, { useState, useMemo } from "react";
import "./SupervisioneFerieAdmin.css";
import { useCollection } from "../../hooks/useCollection";
import { format } from "date-fns";
import Select from "react-select";

export default function SupervisioneFerieAdmin() {
  const { documents: ferieRequests } = useCollection("ferie", null, [
    "createdAt",
    "desc",
  ]);
  const { documents: users } = useCollection("users");

  const [filterUser, setFilterUser] = useState(null);
  const [filterSector, setFilterSector] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterDateStart, setFilterDateStart] = useState("");
  const [filterDateEnd, setFilterDateEnd] = useState("");

  // Options for filters
  const userOptions = useMemo(() => {
    if (!users) return [];
    return users.map((u) => ({
      value: u.id,
      label: u.displayName || u.email,
      sector: u.anagrafica?.settore,
    }));
  }, [users]);

  const sectorOptions = useMemo(() => {
    if (!users) return [];
    const sectors = new Set(
      users.map((u) => u.anagrafica?.settore).filter(Boolean)
    );
    return Array.from(sectors).map((s) => ({ value: s, label: s }));
  }, [users]);

  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "confirmed", label: "Accettata" },
    { value: "rejected", label: "Rifiutata" },
  ];

  // Filter Logic
  const filteredRequests = useMemo(() => {
    if (!ferieRequests) return [];

    return ferieRequests.filter((req) => {
      // 1. User Filter
      if (filterUser && req.userId !== filterUser.value) return false;

      // 2. Sector Filter
      if (filterSector) {
        // Need to find user sector from users list
        const reqUser = users?.find((u) => u.id === req.userId);
        if (reqUser?.anagrafica?.settore !== filterSector.value) return false;
      }

      // 3. Status Filter
      if (filterStatus && req.status !== filterStatus.value) return false;

      // 4. Date Range Filter (Overlapping check)
      if (filterDateStart || filterDateEnd) {
        const reqStart = req.startDate.toDate();
        const reqEnd = req.endDate.toDate();

        if (filterDateStart) {
          // If request ends before filter start, skip
          if (reqEnd < new Date(filterDateStart)) return false;
        }
        if (filterDateEnd) {
          // If request starts after filter end, skip
          if (reqStart > new Date(filterDateEnd)) return false;
        }
      }

      return true;
    });
  }, [
    ferieRequests,
    users,
    filterUser,
    filterSector,
    filterStatus,
    filterDateStart,
    filterDateEnd,
  ]);

  return (
    <div className="supervisione-ferie-container">
      <h1>Supervisione Ferie</h1>

      <div className="filters-card">
        <div className="filter-row">
          <div className="filter-group">
            <label>Utente</label>
            <Select
              options={userOptions}
              value={filterUser}
              onChange={setFilterUser}
              isClearable
              placeholder="Tutti gli utenti"
            />
          </div>
          <div className="filter-group">
            <label>Settore</label>
            <Select
              options={sectorOptions}
              value={filterSector}
              onChange={setFilterSector}
              isClearable
              placeholder="Tutti i settori"
            />
          </div>
          <div className="filter-group">
            <label>Stato</label>
            <Select
              options={statusOptions}
              value={filterStatus}
              onChange={setFilterStatus}
              isClearable
              placeholder="Tutti gli stati"
            />
          </div>
        </div>
        <div className="filter-row">
          <div className="filter-group">
            <label>Dal</label>
            <input
              type="date"
              value={filterDateStart}
              onChange={(e) => setFilterDateStart(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>Al</label>
            <input
              type="date"
              value={filterDateEnd}
              onChange={(e) => setFilterDateEnd(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="results-card">
        <h2>Risultati ({filteredRequests.length})</h2>
        <div className="table-responsive">
          <table className="supervisione-table">
            <thead>
              <tr>
                <th>Dipendente</th>
                <th>Settore</th>
                <th>Periodo</th>
                <th>Stato</th>
                <th>Note</th>
                <th>Creata il</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((req) => {
                const reqUser = users?.find((u) => u.id === req.userId);
                const sector = reqUser?.anagrafica?.settore || "-";
                return (
                  <tr key={req.id}>
                    <td>{req.userName}</td>
                    <td>{sector}</td>
                    <td>
                      {format(req.startDate.toDate(), "dd/MM/yyyy")} -{" "}
                      {format(req.endDate.toDate(), "dd/MM/yyyy")}
                    </td>
                    <td>
                      <span className={`status-badge status-${req.status}`}>
                        {req.status}
                      </span>
                    </td>
                    <td>{req.note}</td>
                    <td>
                      {format(req.createdAt.toDate(), "dd/MM/yyyy HH:mm")}
                    </td>
                  </tr>
                );
              })}
              {filteredRequests.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center">
                    Nessuna richiesta trovata
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
